/**
 * Covers a game scene while its art loads and the canvas builds, so the
 * player sees a branded card with the game's name and a filling bar
 * instead of an empty dark canvas. Tinted with the theme's palette.
 * Sits under the wallet pill and back button (z 45 / 46) so the player
 * can always leave.
 */
export function SceneLoadingOverlay({
  name,
  accent,
  secondary,
  progress,
}: {
  name: string;
  accent: number;
  secondary: number;
  /** 0..1 while art downloads; null when the loader can't measure it. */
  progress: number | null;
}) {
  const hexAccent = toHex(accent);
  const hexSecondary = toHex(secondary);
  const width = progress === null ? null : Math.max(0.06, Math.min(1, progress));
  return (
    <div
      role="status"
      aria-label={`Loading ${name}`}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 44,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        background: `radial-gradient(ellipse at 50% 38%, ${hexSecondary}55 0%, #140826 72%)`,
        fontFamily: 'Fredoka, -apple-system, system-ui',
        color: '#fff',
        animation: 'scene-loading-in 200ms ease-out both',
      }}
    >
      <style>{`
        @keyframes scene-loading-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scene-loading-coin {
          0%   { transform: rotateY(0deg); }
          100% { transform: rotateY(360deg); }
        }
        @keyframes scene-loading-sweep {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(250%); }
        }
      `}</style>
      <div
        aria-hidden
        style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: 'radial-gradient(circle at 35% 30%, #fff3b0 0%, #ffd666 35%, #d99a1e 100%)',
          boxShadow: `0 0 28px ${hexAccent}88, inset 0 -4px 0 rgba(0,0,0,0.18)`,
          animation: 'scene-loading-coin 1.4s linear infinite',
        }}
      />
      <div
        style={{
          fontSize: 26,
          fontWeight: 800,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          textAlign: 'center',
          padding: '0 24px',
          textShadow: `0 2px 12px ${hexAccent}99`,
        }}
      >
        {name}
      </div>
      <div
        style={{
          width: 200,
          height: 8,
          borderRadius: 4,
          background: 'rgba(255,255,255,0.14)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={
            width === null
              ? {
                  position: 'absolute',
                  inset: 0,
                  width: '40%',
                  background: `linear-gradient(90deg, ${hexAccent}, #ffd666)`,
                  animation: 'scene-loading-sweep 1.1s ease-in-out infinite',
                }
              : {
                  height: '100%',
                  width: `${width * 100}%`,
                  background: `linear-gradient(90deg, ${hexAccent}, #ffd666)`,
                  transition: 'width 250ms ease-out',
                }
          }
        />
      </div>
    </div>
  );
}

function toHex(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}
